
import {
  computed, defineComponent, onMounted, ref, Ref, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import {
  ICaseFilesManage,
  ICropCoordinates, IImageCrop, IPhoto, ISharedLable,
} from '@/types/interfaces';
import {
  BOTTOM_POPUP_TYPES, MEDIA_TYPES, NAVIGATION_PAGES, PHOTO_VIEW_TYPE, SHARE_TYPES,
} from '@/types/enums';
import { useDriveStore } from '../modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import AppCropper from '@/components/AppCropper.vue';
import AppSmallLoader from './UI/AppSmallLoader.vue';
import SharePopUp from './BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import MediaSettingsPopUp from './BottomPopUp/PopUpTypes/Settings/MediaSettingsPopUp.vue';
import MoveToPopUp from './BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import UploadService from '@/services/UploadService';
import DriveSharedBar from '@/modules/drive/components/DriveSharedBar.vue';
import clearPhotoCache from '../helpers/clearPhotoCache';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';

export default defineComponent({
  emits: ['close', 'switch-photo'],
  components: {
    AppCropper,
    AppSmallLoader,
    SharePopUp,
    MediaSettingsPopUp,
    MoveToPopUp,
    DriveSharedBar,
  },
  props: {
    disableSwipe: { type: Boolean, default: false },
    viewOnly: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const spacesStore = useSpacesStore();
    const messagesStore = useMessagesStore();

    const route = useRoute();

    const loader: Ref<string> = ref('Loading photo info');
    const photoLoading: Ref<boolean> = ref(true);

    const isSharePopUp: Ref<boolean> = ref(false);
    const isMediaSettingsPopUp: Ref<boolean> = ref(false);
    const isMoveToPopUp: Ref<boolean> = ref(false);
    const imageRef: Ref<any> = ref(null);
    const aspectRatio = ref(1);

    const cropperRef = ref();

    const mainPhoto = computed(() => `${process.env.VUE_APP_MEDIA_URL}${driveStore.fullscreenPhoto!.fileUrl}?t=${+new Date()}`);

    const isMy = computed(() => driveStore.fullscreenPhoto?.owner?.accountId === mainStore.user.accountId);
    const isNetwork = computed(() => route.meta.isNetwork);
    const navigationPage = computed(() => route.meta.navigationPage);
    const isCase = computed(
      () => navigationPage.value === NAVIGATION_PAGES.DRIVE_CASES
        || navigationPage.value === NAVIGATION_PAGES.CASE_EDIT
        || navigationPage.value === NAVIGATION_PAGES.CASE_VIEW,
    );

    function resolvePhoto(item: { name: string, photo: IPhoto, isGroup: boolean }): string {
      if (item.photo?.url) {
        return lowResUrlByViewTypeByUrl(item.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      if (item.isGroup) {
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/Group.svg');
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    }

    function switchPage(direction: string) {
      photoLoading.value = true;
      emit('switch-photo', direction);
    }

    function openPopUp(type: BOTTOM_POPUP_TYPES) {
      if (isCase.value && isSharePopUp.value) {
        return;
      }
      mainStore.BottomPopUpType = type;
    }

    const fullName = computed(() => {
      if (driveStore.fullscreenPhoto!.ownerId.internalId === mainStore.user.accountId) {
        return 'You';
      }

      const contact = spacesStore.myContacts.find((cont) => cont.user.accountId === driveStore.fullscreenPhoto!.ownerId.internalId);

      if (contact) {
        return `${contact?.user.firstName} ${contact?.user.lastName}`;
      }

      return 'No information';
    });

    const photoUrl = computed(() => {
      if (driveStore.fullscreenPhoto!.ownerId.internalId === mainStore.user.accountId) {
        if (mainStore.user.photo?.url) {
          return `${process.env.VUE_APP_MEDIA_URL}${mainStore.user.photo?.url}/200`;
        }
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/profile.svg');
      }

      const contact = spacesStore.myContacts.find((cont) => cont.user.accountId === driveStore.fullscreenPhoto!.ownerId.internalId);

      if (contact) {
        if (contact.user.photo?.url) {
          return process.env.VUE_APP_MEDIA_URL + contact.user.photo?.url;
        }
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/profile.svg');
      }

      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    const image = ref({} as IImageCrop);

    function openCropper() {
      const fileType = driveStore.fullscreenPhoto!.name.split('.').pop();
      image.value = {
        url: process.env.VUE_APP_MEDIA_URL + driveStore.fullscreenPhoto!.fileUrl,
        fileName: driveStore.fullscreenPhoto!.name,
        fileType: `image/${fileType}`,
      } as IImageCrop;

      cropperRef.value.loader = 'Loading image cropper';
    }

    function handleOnCrop(coordinates: ICropCoordinates) {
      function updateBrowserCache() {
        const photoInPhotoList = driveStore.photos.data.find((photo) => photo.id === driveStore.fullscreenPhoto!.id);
        if (photoInPhotoList) {
          clearPhotoCache(photoInPhotoList);
          driveStore.photos.data = [photoInPhotoList, ...driveStore.photos.data.filter((photo) => photo.id !== driveStore.fullscreenPhoto!.id)];
        }

        if (driveStore.currentCase) {
          const photoInCase = driveStore.currentCase.photos.find((ph) => ph.id === driveStore.fullscreenPhoto!.id);
          if (photoInCase) {
            clearPhotoCache(photoInCase);
            driveStore.currentCase.photos = [photoInCase, ...driveStore.currentCase.photos.filter((photo) => photo.id !== driveStore.fullscreenPhoto!.id)];
          }
        }

        if (driveStore.currentFolder && driveStore.currentFolder.mixedData) {
          const photoInFolder = driveStore.currentFolder.mixedData.find((mix) => mix.id === driveStore.fullscreenPhoto!.id);
          if (photoInFolder) {
            clearPhotoCache(photoInFolder.file!);
            // eslint-disable-next-line max-len
            driveStore.currentFolder.mixedData = [photoInFolder, ...driveStore.currentFolder.mixedData.filter((mix) => mix.id !== driveStore.fullscreenPhoto!.id)];
          }
        }
        const photoInMixed = driveStore.mixedData.data.find((mix) => mix.id === driveStore.fullscreenPhoto!.id);
        if (photoInMixed) {
          clearPhotoCache(photoInMixed.file!);
          driveStore.mixedData.data = [photoInMixed, ...driveStore.mixedData.data.filter((mix) => mix.id !== driveStore.fullscreenPhoto!.id)];
        }
      }
      loader.value = 'Saving cropped image';

      UploadService.cropPhoto(driveStore.fullscreenPhoto!.id, coordinates)
        .then(() => {
          // NotificationService.notifySuccess();
          updateBrowserCache();
          const newFileUrl = `${driveStore.fullscreenPhoto!.fileUrl}?t=${Date.now()}`;
          driveStore.fullscreenPhoto = { ...driveStore.fullscreenPhoto!, fileUrl: newFileUrl };
        });
      image.value = {} as IImageCrop;
      photoLoading.value = true;
    }

    function setSharedLabels(labels: ISharedLable[]) {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // driveStore.fullscreenPhoto!.sharedLabels = labels;
    }

    function setItemShared() {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // driveStore.fullscreenPhoto!.shared = true
    }

    function openMovePopUp() {
      isMediaSettingsPopUp.value = false;
      isMoveToPopUp.value = true;
    }

    function deletePhoto() {
      const isShared = !!driveStore.fullscreenPhoto!.share;
      const currentConv = !!messagesStore.currentConversation;

      if (isCase.value) {
        const removeFilePayload = {
          id: driveStore.currentCase.id!,
          fileIds: [driveStore.fullscreenPhoto!.id],
        } as ICaseFilesManage;

        driveStore.removeFilesFromCase(removeFilePayload);

        let idx = driveStore.photoList.findIndex((photo) => photo.id === driveStore.fullscreenPhoto?.id);
        if (driveStore.photoList.length >= 1) {
          idx = (idx + 1) % driveStore.photoList.length;
          driveStore.fullscreenPhoto = driveStore.photoList[idx];
          isMediaSettingsPopUp.value = false;
        } else {
          driveStore.fullscreenPhoto = undefined;
        }
      } else if (isShared && currentConv) {
        const label: ISharedLable = {
          name: '',
          photo: {} as IPhoto,
          isGroup: messagesStore.currentConversation!.info.isGroup,
          id: messagesStore.currentConversation!.info.id,
        };

        driveStore.removeSharingInfo(driveStore.fullscreenPhoto!, label);
        driveStore.fullscreenPhoto = undefined;
      } else {
        driveStore.deleteMedia(driveStore.fullscreenPhoto!.id, MEDIA_TYPES.PHOTO);
        driveStore.fullscreenPhoto = undefined;
      }
    }

    function updateLocation(folderId: string | null) {
      driveStore.fullscreenPhoto!.folderId = folderId;
    }

    function photoLoaded() {
      photoLoading.value = false;
      if (imageRef.value) {
        aspectRatio.value = imageRef.value.width / imageRef.value.height;
      }
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      loader.value = '';
    }

    // watch(() => driveStore.fullscreenPhoto?.id, () => {
    //   if (driveStore.fullscreenPhoto?.id) {
    //     fetchPhoto();
    //   }
    // });

    // onMounted(async () => {
    //   fetchPhoto();
    // });

    return {
      mainStore,
      photoUrl,
      fullName,
      isNetwork,
      isMy,
      navigationPage,
      isCase,
      image,
      loader,
      photoLoading,
      cropperRef,
      mainPhoto,
      isSharePopUp,
      isMediaSettingsPopUp,
      isMoveToPopUp,
      driveStore,
      openCropper,
      handleOnCrop,
      resolvePhoto,
      switchPage,
      openPopUp,
      setSharedLabels,
      openMovePopUp,
      deletePhoto,
      updateLocation,
      cancelUpload,
      SHARE_TYPES,
      BOTTOM_POPUP_TYPES,
      MEDIA_TYPES,
      imageRef,
      aspectRatio,
      photoLoaded,
      setItemShared,
    };
  },
});
